import React, { useState } from 'react'
import styled from 'styled-components'
import { ConsumerRTHTable } from '@nexiahome/ui-common/components/web-only/consumerRTHTable'
import { ConsumerRTHGraph } from '@nexiahome/ui-common/components/web-only/consumerRTHGraph'

import { Device } from 'types/Device'

import RotateDeviceMessage from 'pages/shared/RotateDeviceMessage'

import { Panel } from 'components/Panel'
import Spinner from 'components/Spinner'
import ToggleButtonGroup from 'components/ToggleButtonGroup'

import GA4 from 'helpers/ga4'
import Apptentive from 'helpers/apptentive'
import InfoIconTooltip from './InfoIconTooltip'

import BrowserCompatibility from 'helpers/browserCompatibility'
import useOrientation from 'hooks/useOrientation'
import useConsumerRth from 'hooks/useConsumerRth'

import { NoData, screenSize } from 'styles/shared'

const TimeSpans = [
  { key: 'DAY', displayValue: 'DAILY' },
  { key: 'MONTH', displayValue: 'MONTHLY' }
]

type props = {
  device: Device
}

const ConsumerRthPanel = ({ device }: props) => {
  const { dailyData, isDailyDataLoaded, monthlyData, isMonthlyDataLoaded } = useConsumerRth({ deviceId: device.deviceId })
  const [selectedTimeSpan, setSelectedTimeSpan] = useState(TimeSpans[0].key)

  const isMobile = BrowserCompatibility.isMobile(navigator.userAgent)
  const { isPortrait } = useOrientation()

  const onChangeSelectedTimeSpan = (timeSpan) => {
    setSelectedTimeSpan(timeSpan)

    let eventData = { category: 'Customer Detail Page', action: 'select monthly view', label: 'Consumer RTH Monthly View' }
    let apptentiveEvent = 'consumer_rth_monthly_view'

    if (timeSpan === 'DAY') {
      eventData = { category: 'Customer Detail Page', action: 'select daily view', label: 'Consumer RTH Daily View' }
      apptentiveEvent = 'consumer_rth_daily_view'
    }

    GA4.sendEvent(eventData)
    Apptentive.sendEvent(apptentiveEvent)
  }

  return (
    <S.Panel id="customer-runtime-history" scrollToSection>
      <Panel.Header.Left>
        <S.PanelTitle>Customer Runtime History <S.InfoIconTooltip />
        </S.PanelTitle>
      </Panel.Header.Left>
      {isMobile && isPortrait
        ? (
          <RotateDeviceMessage />
        )
        : (
          <S.Container>
            {(!isDailyDataLoaded || !isMonthlyDataLoaded) && <Spinner variant="page" />}
            {(isDailyDataLoaded && isMonthlyDataLoaded) &&
            <>
              <S.ToggleButtonGroup selectedValue={selectedTimeSpan} values={TimeSpans} onChange={(value) => onChangeSelectedTimeSpan(value)} />
              {selectedTimeSpan === 'DAY' &&
                (() => {
                  if (dailyData) {
                    return (
                      <>
                        <S.Subtitle> {dailyData?.subtitle || ''} </S.Subtitle>
                        <ConsumerRTHGraph rthData={dailyData} />
                        <S.Table><ConsumerRTHTable rthData={dailyData} /></S.Table>
                      </>
                    )
                  } else {
                    return (
                      <S.NoData>
                        <h1>no data</h1>
                        <h2>There is no daily data.</h2>
                      </S.NoData>
                    )
                  }
                })()
              }
              {selectedTimeSpan === 'MONTH' &&
                (() => {
                  if (monthlyData) {
                    return (
                      <>
                        <S.Subtitle> {monthlyData?.subtitle || ''} </S.Subtitle>
                        <ConsumerRTHGraph rthData={monthlyData} />
                        <S.Table><ConsumerRTHTable rthData={monthlyData} /></S.Table>
                      </>
                    )
                  } else {
                    return (
                      <S.NoData>
                        <h1>no data</h1>
                        <h2>There is no monthly data.</h2>
                      </S.NoData>
                    )
                  }
                })()
              }
            </>}
          </S.Container>
        )}
    </S.Panel>
  )
}

export default ConsumerRthPanel

const minHeight = '400px'
const S = {
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
  `,
  PanelTitle: styled(Panel.Title)`
    display: flex;
    align-items: center;
`,
  InfoIconTooltip: styled(InfoIconTooltip)`
    display: inline-block;
    padding-top: 10px;
  `,
  ToggleButtonGroup: styled(ToggleButtonGroup)`
  `,
  Container: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 15px;
  `,
  NoData: styled(NoData)`
    min-height: ${minHeight};

    @media ${screenSize.small} {
      min-height: 0;
      padding: 3.3em;
    }
  `,
  Subtitle: styled.div`
    font-weight: bold;
    text-transform: capitalize;
    margin-top: 20px;
  `,
  Table: styled.div`
    @media ${screenSize.medium} {
      & > table {
        td {
          min-width: 0px !important;
          padding: 2px !important;
        }
      }
    }
    @media ${screenSize.small} {
      & > table {
        td {
          min-width: 0px !important;
          padding: 0px !important;
        }
      }
    }
  `
}
